import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore'
import { app } from './firebase'
import PKG from '../../package.json'

const db = getFirestore(app)

// In-memory cache to reduce Firebase reads
const cache = {
  userProfile: null as UserProfile | null,
  userProfileUserId: null as string | null,
  completedDays: new Map<string, boolean>(),
  lastActivityUpdate: 0,
}

// Minimum interval between activity updates (30 seconds)
const ACTIVITY_UPDATE_INTERVAL_MS = 30 * 1000

/**
 * Clear all cached data (useful for testing or logout)
 */
export function clearProfileCache(): void {
  cache.userProfile = null
  cache.userProfileUserId = null
  cache.completedDays.clear()
  cache.lastActivityUpdate = 0
}

export interface UserProfile {
  userId: string
  displayName: string
  createdAt?: any
  updatedAt?: any
  newFeaturesSeen?: boolean
  bantumiPromoSeen?: boolean
  appVersion?: string
  lastSeenAt?: any
  googleId?: string
  googleEmail?: string
  googleName?: string
  googlePhotoURL?: string
}

/**
 * Get user profile by userId (with caching)
 */
export async function getUserProfile(
  userId: string
): Promise<UserProfile | null> {
  try {
    // Return cached profile if available and same user
    if (cache.userProfile && cache.userProfileUserId === userId) {
      return cache.userProfile
    }

    const profileRef = doc(db, 'profiles', userId)
    const profileSnap = await getDoc(profileRef)

    if (!profileSnap.exists()) {
      return null
    }

    const profile = profileSnap.data() as UserProfile

    // Cache the profile
    cache.userProfile = profile
    cache.userProfileUserId = userId

    return profile
  } catch (error) {
    console.error('Error getting user profile:', error)
    return null
  }
}

/**
 * Check if a display name is unique
 */
export async function isDisplayNameUnique(
  displayName: string,
  excludeUserId?: string
): Promise<boolean> {
  try {
    const normalizedName = displayName.trim().toLowerCase()

    if (normalizedName.length < 3) {
      return false
    }

    const profilesRef = collection(db, 'profiles')
    const q = query(
      profilesRef,
      where('displayNameLower', '==', normalizedName)
    )

    const snapshot = await getDocs(q)

    if (snapshot.empty) {
      return true
    }

    // If we're excluding a user (for updates), check if the match is the same user
    if (excludeUserId) {
      return snapshot.docs.every((doc) => doc.id === excludeUserId)
    }

    return false
  } catch (error) {
    console.error('Error checking display name uniqueness:', error)
    return false
  }
}

/**
 * Save user profile with display name
 */
export async function saveUserProfile(
  userId: string,
  displayName: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const trimmedName = displayName.trim()

    // Validate name length
    if (trimmedName.length < 3) {
      return { success: false, error: 'A név legalább 3 karakter legyen' }
    }

    if (trimmedName.length > 20) {
      return { success: false, error: 'A név maximum 20 karakter lehet' }
    }

    // Check for valid characters (letters, numbers, spaces, underscores, hyphens)
    if (!/^[a-zA-Z0-9áéíóöőúüűÁÉÍÓÖŐÚÜŰ_\- ]+$/.test(trimmedName)) {
      return {
        success: false,
        error: 'A név csak betűket, számokat és kötőjelet tartalmazhat',
      }
    }

    // Check uniqueness
    const isUnique = await isDisplayNameUnique(trimmedName, userId)

    if (!isUnique) {
      return { success: false, error: 'Ez a név már foglalt' }
    }

    const profileRef = doc(db, 'profiles', userId)
    const existingProfile = await getDoc(profileRef)

    const profileData: UserProfile = {
      userId,
      displayName: trimmedName,
      updatedAt: serverTimestamp(),
    }

    if (!existingProfile.exists()) {
      const newProfile = {
        ...profileData,
        displayNameLower: trimmedName.toLowerCase(),
        createdAt: serverTimestamp(),
      }
      await setDoc(profileRef, newProfile)
      // Update cache
      cache.userProfile = newProfile as UserProfile
      cache.userProfileUserId = userId
    } else {
      const updatedProfile = {
        ...existingProfile.data(),
        ...profileData,
        displayNameLower: trimmedName.toLowerCase(),
      }
      await setDoc(profileRef, updatedProfile)
      // Update cache
      cache.userProfile = updatedProfile as UserProfile
      cache.userProfileUserId = userId
    }

    return { success: true }
  } catch (error) {
    console.error('Error saving user profile:', error)
    return { success: false, error: 'Hiba a mentés során' }
  }
}

/**
 * Get display name for a user (returns 'Anonim' if not set)
 */
export async function getDisplayName(userId: string): Promise<string> {
  const profile = await getUserProfile(userId)
  return profile?.displayName || 'Anonim'
}

/**
 * Check if user has seen the new features modal
 */
export async function hasSeenNewFeatures(userId: string): Promise<boolean> {
  try {
    const profile = await getUserProfile(userId)
    return profile?.newFeaturesSeen === true
  } catch (error) {
    console.error('Error checking new features seen:', error)
    return false
  }
}

/**
 * Mark new features as seen for user
 */
export async function markNewFeaturesSeen(userId: string): Promise<void> {
  try {
    const profileRef = doc(db, 'profiles', userId)
    const existingProfile = await getDoc(profileRef)

    if (existingProfile.exists()) {
      const updatedProfile = {
        ...existingProfile.data(),
        newFeaturesSeen: true,
        updatedAt: serverTimestamp(),
      }
      await setDoc(profileRef, updatedProfile, { merge: true })
      // Update cache
      cache.userProfile = updatedProfile as UserProfile
      cache.userProfileUserId = userId
    } else {
      // Create minimal profile if it doesn't exist
      const newProfile = {
        userId,
        displayName: '',
        newFeaturesSeen: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
      await setDoc(profileRef, newProfile)
      // Update cache
      cache.userProfile = newProfile as UserProfile
      cache.userProfileUserId = userId
    }
  } catch (error) {
    console.error('Error marking new features as seen:', error)
  }
}

/**
 * Check if user has seen the Bantumi promo modal
 */
export async function hasSeenBantumiPromo(userId: string): Promise<boolean> {
  try {
    const profile = await getUserProfile(userId)
    return profile?.bantumiPromoSeen === true
  } catch (error) {
    console.error('Error checking bantumi promo seen:', error)
    return false
  }
}

/**
 * Mark Bantumi promo as seen for user
 */
export async function markBantumiPromoSeen(userId: string): Promise<void> {
  try {
    const profileRef = doc(db, 'profiles', userId)
    await setDoc(profileRef, { bantumiPromoSeen: true, updatedAt: serverTimestamp() }, { merge: true })
    // Update cache
    if (cache.userProfileUserId === userId && cache.userProfile) {
      cache.userProfile = { ...cache.userProfile, bantumiPromoSeen: true }
    }
  } catch (error) {
    console.error('Error marking bantumi promo as seen:', error)
  }
}

/**
 * Update user's app version and last seen timestamp
 * Called on app load to track which version users are on
 * Rate-limited to prevent too many writes
 */
export async function updateUserActivity(userId: string): Promise<void> {
  try {
    // Rate limit: don't update more than once every 5 minutes
    const now = Date.now()
    if (now - cache.lastActivityUpdate < ACTIVITY_UPDATE_INTERVAL_MS) {
      console.log('Skipping activity update - too soon')
      return
    }
    cache.lastActivityUpdate = now

    const profileRef = doc(db, 'profiles', userId)
    const existingProfile = await getDoc(profileRef)

    const updateData = {
      appVersion: PKG.version,
      lastSeenAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    if (existingProfile.exists()) {
      await setDoc(profileRef, updateData, { merge: true })
      // Update cache
      cache.userProfile = {
        ...existingProfile.data(),
        ...updateData,
      } as UserProfile
      cache.userProfileUserId = userId
    } else {
      // Create minimal profile if it doesn't exist
      const newProfile = {
        userId,
        displayName: '',
        ...updateData,
        createdAt: serverTimestamp(),
      }
      await setDoc(profileRef, newProfile)
      // Update cache
      cache.userProfile = newProfile as UserProfile
      cache.userProfileUserId = userId
    }
  } catch (error) {
    console.error('Error updating user activity:', error)
  }
}

/**
 * CompletedDay tracking - prevents users from replaying daily games
 */
export interface CompletedDay {
  dayIndex: number
  difficulty: number
  completedAt: any
  score?: number
}

/**
 * Check if a user has already completed a specific day (with caching)
 */
export async function hasCompletedDay(
  userId: string,
  difficulty: number,
  dayIndex: number
): Promise<boolean> {
  try {
    const cacheKey = `${userId}_${difficulty}_${dayIndex}`

    // Return cached result if available
    if (cache.completedDays.has(cacheKey)) {
      return cache.completedDays.get(cacheKey)!
    }

    const completedRef = doc(
      db,
      'profiles',
      userId,
      'completedDays',
      `${difficulty}_${dayIndex}`
    )
    const completedSnap = await getDoc(completedRef)
    const isCompleted = completedSnap.exists()

    // Cache the result
    cache.completedDays.set(cacheKey, isCompleted)

    return isCompleted
  } catch (error) {
    console.error('Error checking completed day:', error)
    return false
  }
}

/**
 * Mark a day as completed for a user
 * Returns false if already completed (prevents cheating)
 */
export async function markDayAsCompleted(
  userId: string,
  difficulty: number,
  dayIndex: number,
  score?: number
): Promise<boolean> {
  try {
    const cacheKey = `${userId}_${difficulty}_${dayIndex}`

    // Check cache first
    if (cache.completedDays.get(cacheKey) === true) {
      console.warn('Day already completed (cached)')
      return false
    }

    const completedRef = doc(
      db,
      'profiles',
      userId,
      'completedDays',
      `${difficulty}_${dayIndex}`
    )

    // Check if already completed
    const existingSnap = await getDoc(completedRef)
    if (existingSnap.exists()) {
      console.warn('Day already completed, cannot submit again')
      cache.completedDays.set(cacheKey, true)
      return false
    }

    // Mark as completed
    await setDoc(completedRef, {
      dayIndex,
      difficulty,
      completedAt: serverTimestamp(),
      score: score ?? 0,
    })

    // Update cache
    cache.completedDays.set(cacheKey, true)

    return true
  } catch (error) {
    console.error('Error marking day as completed:', error)
    return false
  }
}

/**
 * Get all completed days for a user and difficulty
 */
export async function getCompletedDays(
  userId: string,
  difficulty: number
): Promise<number[]> {
  try {
    const completedRef = collection(db, 'profiles', userId, 'completedDays')
    const q = query(completedRef, where('difficulty', '==', difficulty))
    const snapshot = await getDocs(q)

    return snapshot.docs.map((doc) => (doc.data() as CompletedDay).dayIndex)
  } catch (error) {
    console.error('Error getting completed days:', error)
    return []
  }
}

/**
 * Get userId by Google ID
 * Used for automatic login with Google account
 */
export async function getUserIdByGoogleId(
  googleId: string
): Promise<string | null> {
  try {
    const profilesRef = collection(db, 'profiles')
    const q = query(profilesRef, where('googleId', '==', googleId))
    const snapshot = await getDocs(q)

    if (snapshot.empty) {
      return null
    }

    // Return the userId (document ID) of the first match
    return snapshot.docs[0].id
  } catch (error) {
    console.error('Error getting userId by Google ID:', error)
    return null
  }
}

/**
 * Link Google account to user profile
 */
export async function linkGoogleAccount(
  userId: string,
  googleUser: any
): Promise<{ success: boolean; error?: string }> {
  try {
    if (!googleUser || !googleUser.uid) {
      return { success: false, error: 'Érvénytelen Google felhasználó' }
    }

    // Check if this Google account is already linked to another user
    const profilesRef = collection(db, 'profiles')
    const q = query(profilesRef, where('googleId', '==', googleUser.uid))
    const snapshot = await getDocs(q)

    if (!snapshot.empty) {
      const existingProfile = snapshot.docs[0]
      if (existingProfile.id !== userId) {
        return {
          success: false,
          error: 'Ez a Google fiók már össze van kapcsolva egy másik profillal',
        }
      }
    }

    const profileRef = doc(db, 'profiles', userId)
    await setDoc(
      profileRef,
      {
        googleId: googleUser.uid,
        googleEmail: googleUser.email || '',
        googleName: googleUser.displayName || '',
        googlePhotoURL: googleUser.photoURL || '',
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    )

    // Clear cache to force reload
    clearProfileCache()

    return { success: true }
  } catch (error) {
    console.error('Error linking Google account:', error)
    return { success: false, error: 'Hiba történt az összekapcsolás során' }
  }
}

/**
 * Unlink Google account from user profile
 */
export async function unlinkGoogleAccount(
  userId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const profileRef = doc(db, 'profiles', userId)
    await setDoc(
      profileRef,
      {
        googleId: null,
        googleEmail: null,
        googleName: null,
        googlePhotoURL: null,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    )

    // Clear cache to force reload
    clearProfileCache()

    return { success: true }
  } catch (error) {
    console.error('Error unlinking Google account:', error)
    return { success: false, error: 'Hiba történt a leválasztás során' }
  }
}

/**
 * Delete user's game state, profile, and stats from Firebase (for cleanup when switching profiles)
 */
export async function deleteUserState(
  userId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Import deleteDoc here to avoid circular dependency
    const { deleteDoc } = await import('firebase/firestore')

    // Delete the state document from 'states' collection
    const stateRef = doc(db, 'states', userId)
    await deleteDoc(stateRef).catch((err) => {
      // If document doesn't exist, that's fine
      if (err.code !== 'not-found') {
        console.warn('Error deleting state:', err)
      }
    })

    // Delete completed days subcollection from 'profiles/{userId}/completedDays'
    try {
      const completedDaysRef = collection(
        db,
        'profiles',
        userId,
        'completedDays'
      )
      const completedDaysSnapshot = await getDocs(completedDaysRef)
      const deletePromises = completedDaysSnapshot.docs.map((docSnapshot) =>
        deleteDoc(docSnapshot.ref)
      )
      await Promise.all(deletePromises)
      console.log(`Deleted ${deletePromises.length} completedDays documents`)
    } catch (err) {
      console.warn('Error deleting completedDays:', err)
    }

    // Delete the profile document from 'profiles' collection
    const profileRef = doc(db, 'profiles', userId)
    await deleteDoc(profileRef).catch((err) => {
      // If document doesn't exist, that's fine
      if (err.code !== 'not-found') {
        console.warn('Error deleting profile:', err)
      }
    })

    console.log('Deleted state, profile, and stats for userId:', userId)
    return { success: true }
  } catch (error) {
    console.error('Error deleting user data:', error)
    return { success: false, error: 'Hiba történt az adatok törlése során' }
  }
}
